import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, CheckCircle2, Building2, Server, HardDrive } from 'lucide-react';
import {
    createClient,
    createWorkspace,
    createAsset,
    resetOnboarding,
    clearError,
    fetchClients
} from '../../../features/onboarding/onboardingSlice';

export default function OnboardingModal({ onClose, onComplete }) {
    const dispatch = useDispatch();
    const {
        currentStep,
        loading,
        error,
        createdClient,
        createdWorkspaces,
        createClientStatus,
        createWorkspaceStatus,
        createAssetStatus
    } = useSelector((state) => state.onboarding);

    // Step 1: Client data
    const [clientData, setClientData] = useState({
        clientName: '',
        description: ''
    });

    // Step 2: Workspace data (for current workspace being added)
    const [workspaceData, setWorkspaceData] = useState({
        workspaceID: '',
        workspaceName: '',
        description: ''
    });

    // Step 3: Asset data
    const [assetData, setAssetData] = useState({
        deviceName: '',
        deviceType: '',
        ip: '',
        hostname: '',
        logTypeMethod: '',
        selectedWorkspaces: []
    });

    // Form validation errors
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Reset onboarding state when modal opens
        dispatch(resetOnboarding());
    }, [dispatch]);

    useEffect(() => {
        // Clear errors when they're displayed
        if (error) {
            const timer = setTimeout(() => {
                dispatch(clearError());
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, dispatch]);

    // Validate Step 1
    const validateStep1 = () => {
        const newErrors = {};
        if (!clientData.clientName.trim()) {
            newErrors.clientName = 'Client name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate Step 2 (workspace addition)
    const validateWorkspaceAdd = () => {
        const newErrors = {};
        if (!workspaceData.workspaceID.trim()) {
            newErrors.workspaceID = 'Workspace ID is required';
        }
        if (!workspaceData.workspaceName.trim()) {
            newErrors.workspaceName = 'Workspace name is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate Step 2 (proceeding to step 3)
    const validateStep2 = () => {
        const newErrors = {};
        if (createdWorkspaces.length === 0) {
            newErrors.workspaces = 'Please create at least one workspace';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Validate Step 3
    const validateStep3 = () => {
        const newErrors = {};
        if (!assetData.deviceName.trim()) {
            newErrors.deviceName = 'Device name is required';
        }
        if (assetData.selectedWorkspaces.length === 0) {
            newErrors.selectedWorkspaces = 'Please select at least one workspace';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle Step 1 Submit
    const handleStep1Submit = async (e) => {
        e.preventDefault();
        if (!validateStep1() || loading) return;

        try {
            await dispatch(createClient(clientData)).unwrap();
            // Redux will automatically set currentStep to 2
        } catch (err) {
            console.error('Failed to create client:', err);
        }
    };

    // Handle adding a workspace in Step 2
    const handleAddWorkspace = async () => {
        if (!validateWorkspaceAdd() || loading || !createdClient) return;

        try {
            await dispatch(
                createWorkspace({
                    clientUUID: createdClient.clientUUID,
                    workspaceID: workspaceData.workspaceID,
                    workspaceName: workspaceData.workspaceName,
                    description: workspaceData.description
                })
            ).unwrap();

            // Clear workspace form
            setWorkspaceData({ workspaceID: '', workspaceName: '', description: '' });
            setErrors({});
        } catch (err) {
            console.error('Failed to create workspace:', err);
        }
    };

    // Handle Step 2 Continue (proceed to step 3)
    const handleStep2Continue = () => {
        if (!validateStep2()) return;
        // Manually set step to 3 (not using dispatch since it's local navigation)
        dispatch({ type: 'onboarding/setStep', payload: 3 });
    };

    // Handle Step 3 Submit (final step)
    const handleStep3Submit = async (e) => {
        e.preventDefault();
        if (!validateStep3() || loading || !createdClient) return;

        try {
            await dispatch(
                createAsset({
                    clientUUID: createdClient.clientUUID,
                    workspaceUUIDs: assetData.selectedWorkspaces,
                    deviceName: assetData.deviceName,
                    deviceType: assetData.deviceType || undefined,
                    ip: assetData.ip || undefined,
                    hostname: assetData.hostname || undefined,
                    logTypeMethod: assetData.logTypeMethod || undefined
                })
            ).unwrap();

            // Refresh clients list
            dispatch(fetchClients());

            // Notify parent and close
            if (typeof onComplete === 'function') {
                onComplete();
            }
            onClose();
        } catch (err) {
            console.error('Failed to create asset:', err);
        }
    };

    // Toggle workspace selection
    const toggleWorkspaceSelection = (workspaceUUID) => {
        setAssetData((prev) => ({
            ...prev,
            selectedWorkspaces: prev.selectedWorkspaces.includes(workspaceUUID)
                ? prev.selectedWorkspaces.filter((id) => id !== workspaceUUID)
                : [...prev.selectedWorkspaces, workspaceUUID]
        }));
    };

    // Go back to previous step
    const handleBack = () => {
        if (currentStep > 1) {
            dispatch({ type: 'onboarding/setStep', payload: currentStep - 1 });
            setErrors({});
        }
    };

    const handleInputChange = (field, value, dataType = 'client') => {
        if (dataType === 'client') {
            setClientData((prev) => ({ ...prev, [field]: value }));
        } else if (dataType === 'workspace') {
            setWorkspaceData((prev) => ({ ...prev, [field]: value }));
        } else if (dataType === 'asset') {
            setAssetData((prev) => ({ ...prev, [field]: value }));
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const steps = [
        { number: 1, label: 'Client Info', icon: Building2 },
        { number: 2, label: 'Workspace', icon: Server },
        { number: 3, label: 'Device', icon: HardDrive }
    ];

    return (
        <div className="modal-overlay">
            <div className="modal modal--large">
                <div className="modal__header">
                    <div className="modal__title">
                        <Building2 size={20} />
                        Client Onboarding
                    </div>
                    <button className="modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                {/* Step Indicator */}
                <div className="step-indicator">
                    {steps.map((step, index) => {
                        const IconComponent = step.icon;
                        const isActive = currentStep === step.number;
                        const isCompleted = currentStep > step.number;

                        return (
                            <React.Fragment key={step.number}>
                                <div
                                    className={`step-indicator__step ${isActive ? 'step-indicator__step--active' : ''
                                        } ${isCompleted ? 'step-indicator__step--completed' : ''}`}
                                >
                                    <div className="step-indicator__icon">
                                        {isCompleted ? (
                                            <CheckCircle2 size={20} />
                                        ) : (
                                            <IconComponent size={20} />
                                        )}
                                    </div>
                                    <div className="step-indicator__label">
                                        <div className="step-indicator__number">Step {step.number}</div>
                                        <div className="step-indicator__text">{step.label}</div>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div
                                        className={`step-indicator__line ${isCompleted ? 'step-indicator__line--completed' : ''
                                            }`}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>

                {error && (
                    <div className="form-error-alert" style={{ margin: '0 20px' }}>
                        {error}
                    </div>
                )}

                <div className="modal__content">
                    {/* Step 1: Client Information */}
                    {currentStep === 1 && (
                        <form onSubmit={handleStep1Submit}>
                            <div className="form-section">
                                <h4 className="form-section__title">Client Information</h4>

                                <div className="form-field">
                                    <label className="form-field__label">Client Name *</label>
                                    <input
                                        type="text"
                                        value={clientData.clientName}
                                        onChange={(e) =>
                                            handleInputChange('clientName', e.target.value, 'client')
                                        }
                                        className={`form-field__input ${errors.clientName ? 'form-field__input--error' : ''
                                            }`}
                                        placeholder="Enter client name"
                                    />
                                    {errors.clientName && (
                                        <span className="form-field__error">{errors.clientName}</span>
                                    )}
                                </div>

                                <div className="form-field">
                                    <label className="form-field__label">Description</label>
                                    <textarea
                                        value={clientData.description}
                                        onChange={(e) =>
                                            handleInputChange('description', e.target.value, 'client')
                                        }
                                        className="form-field__input"
                                        placeholder="Enter client description (optional)"
                                        rows="3"
                                    />
                                </div>
                            </div>
                        </form>
                    )}

                    {/* Step 2: Workspaces */}
                    {currentStep === 2 && (
                        <div className="form-section">
                            <h4 className="form-section__title">Add Workspaces</h4>
                            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '16px' }}>
                                Client: <strong style={{ color: '#e2e8f0' }}>{createdClient?.clientName}</strong>
                            </p>


                            <div className="form-field">
                                <label className="form-field__label">Workspace ID *</label>
                                <input
                                    type="text"
                                    value={workspaceData.workspaceID}
                                    onChange={(e) =>
                                        handleInputChange('workspaceID', e.target.value, 'workspace')
                                    }
                                    className={`form-field__input ${errors.workspaceID ? 'form-field__input--error' : ''
                                        }`}
                                    placeholder="e.g., 78397a81-fb1f-414d-a7e2-e78e7df2412b"
                                />
                                {errors.workspaceID && (
                                    <span className="form-field__error">{errors.workspaceID}</span>
                                )}
                            </div>
                            <div className="form-field">
                                <label className="form-field__label">Workspace Name *</label>
                                <input
                                    type="text"
                                    value={workspaceData.workspaceName}
                                    onChange={(e) =>
                                        handleInputChange('workspaceName', e.target.value, 'workspace')
                                    }
                                    className={`form-field__input ${errors.workspaceName ? 'form-field__input--error' : ''
                                        }`}
                                    placeholder="e.g., Production Environment"
                                />
                                {errors.workspaceName && (
                                    <span className="form-field__error">{errors.workspaceName}</span>
                                )}
                            </div>

                            <div className="form-field">
                                <label className="form-field__label">Description</label>
                                <input
                                    type="text"
                                    value={workspaceData.description}
                                    onChange={(e) =>
                                        handleInputChange('description', e.target.value, 'workspace')
                                    }
                                    className="form-field__input"
                                    placeholder="Workspace description (optional)"
                                />
                            </div>

                            <button
                                type="button"
                                onClick={handleAddWorkspace}
                                className="btn btn--primary"
                                disabled={loading || !workspaceData.workspaceID.trim() || !workspaceData.workspaceName.trim()}
                                style={{ marginTop: '12px' }}
                            >
                                {createWorkspaceStatus === 'loading' ? 'Adding...' : 'Add Workspace'}
                            </button>

                            {/* Created Workspaces List */}
                            {createdWorkspaces.length > 0 && (
                                <div className="workspace-list">
                                    <h5 className="workspace-list__title">
                                        Created Workspaces ({createdWorkspaces.length})
                                    </h5>
                                    {createdWorkspaces.map((ws) => (
                                        <div key={ws.workspaceUUID} className="workspace-list__item">
                                            <div>
                                                <div className="workspace-list__name">{ws.workspaceName}</div>
                                                {ws.description && (
                                                    <div className="workspace-list__description">
                                                        {ws.description}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="chip chip--info">ID: {ws.workspaceID}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {errors.workspaces && (
                                <div className="form-field__error" style={{ marginTop: '12px' }}>
                                    {errors.workspaces}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Device/Asset Information */}
                    {currentStep === 3 && (
                        <form onSubmit={handleStep3Submit}>
                            <div className="form-section">
                                <h4 className="form-section__title">Device Information</h4>

                                {/* Workspace Selection */}
                                <div className="form-field">
                                    <label className="form-field__label">Select Workspaces *</label>
                                    <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                                        Select one or more workspaces for this device
                                    </p>
                                    <div className="workspace-checkbox-group">
                                        {createdWorkspaces.map((ws) => (
                                            <label key={ws.workspaceUUID} className="workspace-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={assetData.selectedWorkspaces.includes(
                                                        ws.workspaceUUID
                                                    )}
                                                    onChange={() => toggleWorkspaceSelection(ws.workspaceUUID)}
                                                />
                                                <span>{ws.workspaceName}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {errors.selectedWorkspaces && (
                                        <span className="form-field__error">
                                            {errors.selectedWorkspaces}
                                        </span>
                                    )}
                                </div>

                                <div className="form-row">
                                    <div className="form-field">
                                        <label className="form-field__label">Device Name *</label>
                                        <input
                                            type="text"
                                            value={assetData.deviceName}
                                            onChange={(e) =>
                                                handleInputChange('deviceName', e.target.value, 'asset')
                                            }
                                            className={`form-field__input ${errors.deviceName ? 'form-field__input--error' : ''
                                                }`}
                                            placeholder="e.g., Server-PROD-01"
                                        />
                                        {errors.deviceName && (
                                            <span className="form-field__error">{errors.deviceName}</span>
                                        )}
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field__label">Device Type</label>
                                        <input
                                            type="text"
                                            value={assetData.deviceType}
                                            onChange={(e) =>
                                                handleInputChange('deviceType', e.target.value, 'asset')
                                            }
                                            className="form-field__input"
                                            placeholder="e.g., Windows Server"
                                        />
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-field">
                                        <label className="form-field__label">IP Address</label>
                                        <input
                                            type="text"
                                            value={assetData.ip}
                                            onChange={(e) => handleInputChange('ip', e.target.value, 'asset')}
                                            className="form-field__input"
                                            placeholder="e.g., 192.168.1.100"
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label className="form-field__label">Hostname</label>
                                        <input
                                            type="text"
                                            value={assetData.hostname}
                                            onChange={(e) =>
                                                handleInputChange('hostname', e.target.value, 'asset')
                                            }
                                            className="form-field__input"
                                            placeholder="e.g., server01.local"
                                        />
                                    </div>
                                </div>

                                <div className="form-field">
                                    <label className="form-field__label">LogType Method</label>
                                    <input
                                        type="text"
                                        value={assetData.logTypeMethod}
                                        onChange={(e) =>
                                            handleInputChange('logTypeMethod', e.target.value, 'asset')
                                        }
                                        className="form-field__input"
                                        placeholder="e.g., Syslog, WinEvent"
                                    />
                                </div>
                            </div>
                        </form>
                    )}
                </div>

                <div className="modal__footer">
                    {currentStep > 1 && (
                        <button
                            type="button"
                            className="btn btn--outline"
                            onClick={handleBack}
                            disabled={loading}
                        >
                            Back
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn--outline"
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {currentStep === 1 && (
                        <button
                            type="submit"
                            className="btn btn--primary"
                            onClick={handleStep1Submit}
                            disabled={loading}
                        >
                            {createClientStatus === 'loading' ? 'Creating...' : 'Next: Add Workspaces'}
                        </button>
                    )}
                    {currentStep === 2 && (
                        <button
                            type="button"
                            className="btn btn--primary"
                            onClick={handleStep2Continue}
                            disabled={createdWorkspaces.length === 0}
                        >
                            Next: Add Device
                        </button>
                    )}
                    {currentStep === 3 && (
                        <button
                            type="submit"
                            className="btn btn--primary"
                            onClick={handleStep3Submit}
                            disabled={loading}
                        >
                            {createAssetStatus === 'loading'
                                ? 'Completing...'
                                : 'Complete Onboarding'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
